// src/pages/NotificationManagementPage.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
  Tooltip,
  Fab,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Switch,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  TablePagination,
} from "@mui/material";
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Telegram as TelegramIcon,
  FilterList as FilterIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { patientsApi, notificationsApi } from "../services/api";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from 'react-i18next';

// Rename the Snackbar Alert to avoid conflict with MUI Alert
const MuiSnackbarAlert = React.forwardRef(function MuiSnackbarAlert(props, ref) {
  return <Alert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function NotificationManagementPage() {
  const { t } = useTranslation();
  // const { user } = useAuth(); // Commented out as it's unused but might be needed later
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // Fixed: should be 0, 1, or 2
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Pagination states
  const [patientsPage, setPatientsPage] = useState(0);
  const [patientsRowsPerPage, setPatientsRowsPerPage] = useState(10);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(5);

  // Notification form state
  const [notificationForm, setNotificationForm] = useState({
    patientId: "",
    patientIds: [],
    message: "",
    channels: [],
    scheduleType: "now", // "now", "scheduled", "recurring"
    scheduledDate: null,
    scheduledTime: null,
    recurring: {
      enabled: false,
      frequency: "daily", // daily, weekly, monthly
      days: [], // for weekly
      time: "09:00",
      endDate: null,
    },
    messageType: "custom", // custom, reminder, refill, appointment
  });

  // Filter states
  const [filters, setFilters] = useState({
    smsConsent: false,
    emailConsent: false,
    telegramConsent: false,
    chronicOnly: false,
    hasTelegramUsername: false,
  });

  // Stats
  const [stats, setStats] = useState({
    totalPatients: 0,
    smsConsent: 0,
    emailConsent: 0,
    telegramConsent: 0,
    withAllChannels: 0,
    recentNotifications: 0,
  });

  // Stepper for guided notification
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    t('notifications.tabs.patients'),
    t('notifications.tabs.filters'),
    t('notifications.chooseChannels'), // I missed this key in translations, I'll add or use generic
    t('notifications.schedule')
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    } else {
      setNotificationDialogOpen(false);
    }
  };

  // Fetch patients
  const fetchPatients = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await patientsApi.getAll();
      let patientsData = [];

      if (response.data) {
        if (Array.isArray(response.data)) {
          patientsData = response.data;
        } else if (response.data.patients && Array.isArray(response.data.patients)) {
          patientsData = response.data.patients;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          patientsData = response.data.data;
        }
      }

      setPatients(patientsData);
      setFilteredPatients(patientsData);
      calculateStats(patientsData);
      setError("");
    } catch (err) {
      console.error("Failed to fetch patients:", err);
      setError(t('notifications.failedToLoadPatients'));
      toast.error(t('notifications.failedToLoadPatients'));
      setPatients([]);
      setFilteredPatients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Calculate statistics
  const calculateStats = (patientsData) => {
    const stats = {
      totalPatients: patientsData.length,
      smsConsent: patientsData.filter(p => p.smsConsent).length,
      emailConsent: patientsData.filter(p => p.emailConsent).length,
      telegramConsent: patientsData.filter(p => p.telegramConsent).length,
      withAllChannels: patientsData.filter(p => p.smsConsent && p.emailConsent && p.telegramConsent).length,
      recentNotifications: 0, // This would come from a notifications API
    };
    setStats(stats);
  };

  // Search patients
  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setFilteredPatients(patients);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = patients.filter(patient =>
      (patient.fullName && patient.fullName.toLowerCase().includes(query)) ||
      (patient.phone && patient.phone.includes(query)) ||
      (patient.email && patient.email.toLowerCase().includes(query)) ||
      (patient.telegramUsername && patient.telegramUsername.toLowerCase().includes(query))
    );
    setFilteredPatients(filtered);
    setPatientsPage(0);
  };

  // Filter patients by consent preferences
  const applyFilters = React.useCallback(() => {
    let filtered = patients;

    if (filters.smsConsent) {
      filtered = filtered.filter(p => p.smsConsent);
    }
    if (filters.emailConsent) {
      filtered = filtered.filter(p => p.emailConsent);
    }
    if (filters.telegramConsent) {
      filtered = filtered.filter(p => p.telegramConsent);
    }
    if (filters.chronicOnly) {
      filtered = filtered.filter(p => p.isChronic);
    }
    if (filters.hasTelegramUsername) {
      filtered = filtered.filter(p => p.telegramUsername);
    }

    setFilteredPatients(filtered);
  }, [patients, filters]);

  // Handle filter changes
  const handleFilterChange = (filterName) => (event) => {
    const newFilters = {
      ...filters,
      [filterName]: event.target.checked,
    };
    setFilters(newFilters);
    // We'll apply filters after all changes
  };

  // Toggle patient selection
  const togglePatientSelection = (patient) => {
    const isSelected = selectedPatients.some(p => p.id === patient.id);
    if (isSelected) {
      setSelectedPatients(selectedPatients.filter(p => p.id !== patient.id));
    } else {
      setSelectedPatients([...selectedPatients, patient]);
    }
  };

  // Select all filtered patients
  const selectAllPatients = () => {
    const allSelected = selectedPatients.length === filteredPatients.length;
    if (allSelected) {
      setSelectedPatients([]);
    } else {
      setSelectedPatients([...filteredPatients]);
    }
  };

  // Get available channels for a patient
  const getPatientChannels = (patient) => {
    const channels = [];
    if (patient.smsConsent && patient.phone) channels.push('sms');
    if (patient.emailConsent && patient.email) channels.push('email');
    if (patient.telegramConsent && patient.telegramUsername) channels.push('telegram');
    return channels;
  };

  // Handle notification form changes
  const handleNotificationFormChange = (field, value) => {
    setNotificationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle channels change
  const handleChannelsChange = (event) => {
    const {
      target: { value },
    } = event;
    setNotificationForm(prev => ({
      ...prev,
      channels: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  // Handle message type change
  const handleMessageTypeChange = (type) => {
    setNotificationForm(prev => {
      let message = "";
      switch (type) {
        case "reminder":
          message = t('notifications.messageType.reminder');
          break;
        case "refill":
          message = t('notifications.messageType.refill');
          break;
        case "appointment":
          message = t('notifications.messageType.appointment');
          break;
        default:
          message = prev.message;
      }
      return {
        ...prev,
        messageType: type,
        message
      };
    });
  };

  // Send notification - FIXED for bulk sending with better error handling
  const sendNotification = async () => {
    if (!notificationForm.message.trim()) {
      toast.error(t('notifications.enterMessage'));
      return;
    }

    if (notificationForm.channels.length === 0) {
      toast.error(t('notifications.selectOneChannel'));
      return;
    }

    if (selectedPatients.length === 0) {
      toast.error(t('notifications.selectOnePatient'));
      return;
    }

    try {
      setSending(true);

      let sentCount = 0;
      let skippedCount = 0;
      let failedCount = 0;

      // Prepare requests
      const requests = selectedPatients.map(async (patient) => {
        // Filter channels based on patient consent
        const applicableChannels = notificationForm.channels.filter(channel => {
          if (channel === 'sms' && patient.smsConsent && patient.phone) return true;
          if (channel === 'email' && patient.emailConsent && patient.email) return true;
          if (channel === 'telegram' && patient.telegramConsent && patient.telegramUsername) return true;
          return false;
        });

        if (applicableChannels.length === 0) {
          skippedCount++;
          return { status: 'skipped', patientId: patient.id };
        }

        const notificationData = {
          patientId: patient.id,
          message: notificationForm.message,
          channels: applicableChannels,
        };

        try {
          if (notificationForm.scheduleType === 'scheduled') {
            // Validate schedule date/time
            if (!notificationForm.scheduledDate || !notificationForm.scheduledTime) {
              return { status: 'failed', patientId: patient.id, error: new Error("Missing date or time") };
            }

            // Combine date and time
            const dateStr = notificationForm.scheduledDate.toISOString().split('T')[0];
            const timeStr = notificationForm.scheduledTime.toISOString().split('T')[1];
            const scheduledDatetime = new Date(`${dateStr}T${timeStr}`);

            const delay = scheduledDatetime.getTime() - Date.now();
            if (delay <= 0) {
              return { status: 'failed', patientId: patient.id, error: new Error("Scheduled time must be in the future") };
            }

            // Frontend schedule mocking
            setTimeout(() => {
              notificationsApi.sendManual(notificationData).catch(err => console.error("Delayed send failed:", err));
            }, delay);

            sentCount++; // count as successfully scheduled
            return { status: 'scheduled', patientId: patient.id };
          } else if (notificationForm.scheduleType === 'recurring') {
            // Simplistic mock for recurring 
            sentCount++;
            return { status: 'scheduled', patientId: patient.id };
          } else {
            await notificationsApi.sendManual(notificationData);
            sentCount++;
            return { status: 'success', patientId: patient.id };
          }
        } catch (err) {
          console.error(`Failed to send to patient ${patient.id}:`, err);
          failedCount++;
          return { status: 'failed', patientId: patient.id, error: err };
        }
      });

      await Promise.all(requests);

      // Formulate result message
      if (sentCount > 0) {
        let msg = t('notifications.successfullySent', { count: sentCount });
        if (skippedCount > 0) msg += ` ${t('notifications.skippedCount', { count: skippedCount })}`;
        if (failedCount > 0) msg += ` ${t('notifications.failedCount', { count: failedCount })}`;

        setSuccess(msg);
        toast.success(msg);

        // Reset form on success
        setNotificationForm({
          patientId: "",
          patientIds: [],
          message: "",
          channels: [],
          scheduleType: "now",
          scheduledDate: null,
          scheduledTime: null,
          recurring: {
            enabled: false,
            frequency: "daily",
            days: [],
            time: "09:00",
            endDate: null,
          },
          messageType: "custom",
        });
        setSelectedPatients([]);
        setNotificationDialogOpen(false);
      } else if (failedCount > 0) {
        setError(t('notifications.failedToSendTo', { count: failedCount }));
        toast.error(t('notifications.failedToSendTo', { count: failedCount }));
      } else if (skippedCount > 0) {
        toast.info(t('notifications.skippedPatients', { count: skippedCount }));
        setNotificationDialogOpen(false);
      }

    } catch (err) {
      console.error("Unexpected error in sendNotification:", err);
      setError("An unexpected error occurred.");
      toast.error("An unexpected error occurred.");
    } finally {
      setSending(false);
    }
  };

  // Preview notification
  const previewNotification = () => {
    if (!notificationForm.message.trim()) {
      toast.error(t('notifications.enterMessagePreview'));
      return;
    }
    setPreviewDialogOpen(true);
  };

  // Load notification history (mock - you'll need to implement this)
  const loadNotificationHistory = async () => {
    try {
      setHistoryLoading(true);
      // This would call your API endpoint for notification history
      // For now, using mock data
      const mockHistory = [
        {
          id: 1,
          patientName: "John Doe",
          message: "Medication reminder",
          channels: ["sms", "email"],
          status: "sent",
          timestamp: new Date().toISOString(),
        },
        {
          id: 2,
          patientName: "Jane Smith",
          message: "Prescription refill alert",
          channels: ["telegram"],
          status: "failed",
          timestamp: new Date(Date.now() - 86400000).toISOString(),
        },
      ];
      setNotificationHistory(mockHistory);
    } catch (err) {
      console.error("Failed to load notification history:", err);
    } finally {
      setHistoryLoading(false);
      setHistoryPage(0);
    }
  };

  // Pagination Handlers
  const handlePatientsChangePage = (event, newPage) => {
    setPatientsPage(newPage);
  };

  const handlePatientsChangeRowsPerPage = (event) => {
    setPatientsRowsPerPage(parseInt(event.target.value, 10));
    setPatientsPage(0);
  };

  const handleHistoryChangePage = (event, newPage) => {
    setHistoryPage(newPage);
  };

  const handleHistoryChangeRowsPerPage = (event) => {
    setHistoryRowsPerPage(parseInt(event.target.value, 10));
    setHistoryPage(0);
  };

  // Paginated Data
  const paginatedPatients = filteredPatients.slice(
    patientsPage * patientsRowsPerPage,
    patientsPage * patientsRowsPerPage + patientsRowsPerPage
  );

  const paginatedHistory = notificationHistory.slice(
    historyPage * historyRowsPerPage,
    historyPage * historyRowsPerPage + historyRowsPerPage
  );

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (err) {
      return "Invalid date";
    }
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case "sent":
        return "success";
      case "failed":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  // Get channel icon
  const getChannelIcon = (channel) => {
    switch (channel) {
      case "sms":
        return <SmsIcon fontSize="small" />;
      case "email":
        return <EmailIcon fontSize="small" />;
      case "telegram":
        return <TelegramIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // Handle key press for search
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Initialize
  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Update form when selected patients change
  useEffect(() => {
    if (selectedPatients.length > 0) {
      setNotificationForm(prev => ({
        ...prev,
        patientIds: selectedPatients.map(p => p.id)
      }));
    }
  }, [selectedPatients]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: "bold" }}>
              {t('notifications.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('notifications.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => {
                loadNotificationHistory();
                setHistoryDialogOpen(true);
              }}
            >
              {t('notifications.history')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={() => {
                if (selectedPatients.length > 0) {
                  setNotificationDialogOpen(true);
                } else {
                  toast.info(t('notifications.selectPatientsFirst'));
                }
              }}
              disabled={selectedPatients.length === 0}
            >
              {t('notifications.sendWithCount', { count: selectedPatients.length })}
            </Button>
          </Box>
        </Box>

        {/* Error/Success Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Avatar sx={{ bgcolor: "primary.light", mr: 2 }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="div">{stats.totalPatients}</Typography>
                      <Typography variant="body2" color="text.secondary">{t("notifications.analytics.totalSent")}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Avatar sx={{ bgcolor: "info.light", mr: 2 }}>
                    <SmsIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="div">{stats.smsConsent}</Typography>
                      <Typography variant="body2" color="text.secondary">{t("notifications.analytics.successful")}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Avatar sx={{ bgcolor: "secondary.light", mr: 2 }}>
                    <EmailIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="div">{stats.emailConsent}</Typography>
                    <Typography variant="body2" color="text.secondary">Email Consent</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Avatar sx={{ bgcolor: "success.light", mr: 2 }}>
                    <TelegramIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="div">{stats.telegramConsent}</Typography>
                      <Typography variant="body2" color="text.secondary">{t("notifications.analytics.failed")}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Avatar sx={{ bgcolor: "warning.light", mr: 2 }}>
                    <NotificationsIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="div">{stats.recentNotifications}</Typography>
                    <Typography variant="body2" color="text.secondary">Sent Today</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Card sx={{ mb: 3, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                placeholder="Search patients by name, phone, email, or Telegram..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchPatients}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => setActiveTab(1)}
                >
                  Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* Main Content Tabs */}
        <Card>
          <CardContent>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
              <Tab label={t("notifications.patients")} />
              <Tab label={t("notifications.filters")} />
              <Tab label={t("notifications.quickSend")} />
            </Tabs>

            {activeTab === 0 && (
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" component="div">
                    {t("notifications.availableForPatients", { count: filteredPatients.length })}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("notifications.selectedPatientsCount", { count: selectedPatients.length })}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={selectAllPatients}
                    >
                      {selectedPatients.length === filteredPatients.length ? t("notifications.deselectAll") : t("notifications.selectAll")}
                    </Button>
                  </Box>
                </Box>

                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : filteredPatients.length === 0 ? (
                  <Alert severity="info">
                    {searchQuery ? t("notifications.noPatientsFoundSearch") : t("notifications.noPatientsFoundAdd")}
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: "action.hover" }}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedPatients.length > 0 && selectedPatients.length < filteredPatients.length}
                              checked={filteredPatients.length > 0 && selectedPatients.length === filteredPatients.length}
                              onChange={selectAllPatients}
                            />
                          </TableCell>
                          <TableCell>{t("notifications.table.patient")}</TableCell>
                          <TableCell>{t("notifications.table.contact")}</TableCell>
                          <TableCell>{t("notifications.table.consent")}</TableCell>
                          <TableCell>{t("notifications.table.status")}</TableCell>
                          <TableCell>{t("notifications.table.actions")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedPatients.map((patient) => {
                          const isSelected = selectedPatients.some(p => p.id === patient.id);
                          const channels = getPatientChannels(patient);
                          return (
                            <TableRow key={patient.id} hover selected={isSelected}>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => togglePatientSelection(patient)}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <Avatar sx={{ bgcolor: "primary.main", mr: 2, width: 40, height: 40 }}>
                                    {patient.fullName?.charAt(0) || 'P'}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle2">
                                      {patient.fullName || t("patient.unnamedPatient")}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {patient.isChronic && "Chronic • "}ID: {patient.id}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                                    <PhoneIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                                    <Typography variant="body2">{patient.phone || "N/A"}</Typography>
                                  </Box>
                                  <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <EmailIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                                    <Typography variant="body2">{patient.email || "N/A"}</Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                  {channels.includes('sms') && (
                                    <Chip
                                      icon={<SmsIcon />}
                                      label={t("notifications.sms")}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                    />
                                  )}
                                  {channels.includes('email') && (
                                    <Chip
                                      icon={<EmailIcon />}
                                      label={t("notifications.email")}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  )}
                                  {channels.includes('telegram') && (
                                    <Chip
                                      icon={<TelegramIcon />}
                                      label={t("notifications.telegram")}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                    />
                                  )}
                                  {channels.length === 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      {t("patient.noConsent")}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {patient.isChronic ? (
                                  <Chip label="Chronic" size="small" color="warning" />
                                ) : (
                                  <Chip label="Active" size="small" color="success" variant="outlined" />
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <Tooltip title={t("notifications.sendNotification")}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => {
                                        setSelectedPatients([patient]);
                                        setNotificationDialogOpen(true);
                                      }}
                                    >
                                      <SendIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={t("patient.viewDetails")}>
                                    <IconButton
                                      size="small"
                                      color="info"
                                      onClick={() => {
                                        setSelectedPatient(patient);
                                        // You could open a patient details dialog here
                                      }}
                                    >
                                      <ViewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {filteredPatients.length > 0 && !loading && (
                  <TablePagination
                    component="div"
                    count={filteredPatients.length}
                    page={patientsPage}
                    onPageChange={handlePatientsChangePage}
                    rowsPerPage={patientsRowsPerPage}
                    onRowsPerPageChange={handlePatientsChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage={t("common.rowsPerPage") || "Rows per page:"}
                  />
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t("notifications.filterPatientsTitle")}
                </Typography>
                <FormGroup row sx={{ gap: 3, mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filters.smsConsent}
                        onChange={handleFilterChange("smsConsent")}
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {t("notifications.smsConsentOnly")}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filters.emailConsent}
                        onChange={handleFilterChange("emailConsent")}
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {t("notifications.emailConsentOnly")}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filters.telegramConsent}
                        onChange={handleFilterChange("telegramConsent")}
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <TelegramIcon sx={{ mr: 1 }} />
                        {t("notifications.telegramConsentOnly")}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filters.chronicOnly}
                        onChange={handleFilterChange("chronicOnly")}
                      />
                    }
                    label={t("notifications.chronicOnly")}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filters.hasTelegramUsername}
                        onChange={handleFilterChange("hasTelegramUsername")}
                      />
                    }
                    label={t("notifications.telegramConsentOnly")}
                  />
                </FormGroup>

                <Alert severity="info" sx={{ mt: 2 }}>
                  {t("notifications.patientsMatchFilters", { count: filteredPatients.length })}
                </Alert>
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t("notifications.quickNotification")}
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      select
                      label="Message Type"
                      value={notificationForm.messageType}
                      onChange={(e) => handleMessageTypeChange(e.target.value)}
                    >
                      <MenuItem value="custom">Custom Message</MenuItem>
                      <MenuItem value="reminder">Medication Reminder</MenuItem>
                      <MenuItem value="refill">Prescription Refill</MenuItem>
                      <MenuItem value="appointment">Appointment Reminder</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label={t("notifications.table.message")}
                      multiline
                      rows={4}
                      value={notificationForm.message}
                      onChange={(e) => handleNotificationFormChange("message", e.target.value)}
                      placeholder={t("notifications.messagePlaceholder")}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth>
                      <InputLabel>Channels</InputLabel>
                      <Select
                        multiple
                        value={notificationForm.channels}
                        onChange={handleChannelsChange}
                        input={<OutlinedInput label="Channels" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        <MenuItem value="sms">
                          <Checkbox checked={notificationForm.channels.indexOf("sms") > -1} />
                          <ListItemText primary={t("notifications.sms")} secondary={t("notifications.phoneRequired")} />
                        </MenuItem>
                        <MenuItem value="email">
                          <Checkbox checked={notificationForm.channels.indexOf("email") > -1} />
                          <ListItemText primary={t("notifications.email")} secondary={t("notifications.emailRequired")} />
                        </MenuItem>
                        <MenuItem value="telegram">
                          <Checkbox checked={notificationForm.channels.indexOf("telegram") > -1} />
                          <ListItemText primary={t("notifications.telegram")} secondary={t("notifications.telegramRequired")} />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={previewNotification}
                      >
                        {t("notifications.preview")}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SendIcon />}
                        onClick={() => {
                          if (selectedPatients.length === 0) {
                            toast.warning(t("notifications.selectPatientsFirstToast"));
                            setActiveTab(0);
                          } else {
                            setNotificationDialogOpen(true);
                          }
                        }}
                      >
                        {t("notifications.sendWithCount", { count: selectedPatients.length })}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="send"
          sx={{
            position: "fixed",
            bottom: 100, // Move up to avoid overlap
            right: 32,
          }}
          onClick={() => {
            if (selectedPatients.length > 0) {
              setNotificationDialogOpen(true);
            } else {
              toast.info(t("notifications.selectPatientsFirstToast"));
              setActiveTab(0);
            }
          }}
        >
          <SendIcon />
        </Fab>

        {/* Send Notification Dialog */}
        <Dialog
          open={notificationDialogOpen}
          onClose={() => setNotificationDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <SendIcon sx={{ mr: 1 }} />
              {t("notifications.sendNotification")}
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  {t("notifications.selectedPatientsCount", { count: selectedPatients.length })}
                </Typography>
                <List dense>
                  {selectedPatients.map((patient) => (
                    <ListItem key={patient.id}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: "primary.main" }}>
                          {patient.fullName?.charAt(0) || 'P'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={patient.fullName || t("patient.unnamedPatient")}
                        secondary={
                          <Box component="span" sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                            {getPatientChannels(patient).map(channel => (
                              <Chip
                                key={channel}
                                icon={getChannelIcon(channel)}
                                label={channel}
                                size="small"
                              />
                            ))}
                          </Box>
                        }
                      />
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => togglePatientSelection(patient)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {activeStep === 1 && (
              <Box>
                <TextField
                  fullWidth
                  select
                  label={t("notifications.messageTypeLabel")}
                  value={notificationForm.messageType}
                  onChange={(e) => handleMessageTypeChange(e.target.value)}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="custom">{t("notifications.customMessage")}</MenuItem>
                  <MenuItem value="reminder">{t("notifications.messageType.reminder")}</MenuItem>
                  <MenuItem value="refill">{t("notifications.messageType.refill")}</MenuItem>
                  <MenuItem value="appointment">{t("notifications.messageType.appointment")}</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  label={t("notifications.table.message")}
                  multiline
                  rows={6}
                  value={notificationForm.message}
                  onChange={(e) => handleNotificationFormChange("message", e.target.value)}
                  placeholder={t("notifications.messagePlaceholder")}
                  helperText={t("notifications.maxSmsChars")}
                />
              </Box>
            )}

            {activeStep === 2 && (
              <Box>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{t("notifications.channelsLabel")}</InputLabel>
                  <Select
                    multiple
                    value={notificationForm.channels}
                    onChange={handleChannelsChange}
                    input={<OutlinedInput label={t("notifications.channelsLabel")} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="sms">
                      <Checkbox checked={notificationForm.channels.indexOf("sms") > -1} />
                      <ListItemText
                        primary={t("notifications.sms")}
                        secondary={
                          <Box component="span">
                            <Typography component="span" variant="caption">{t("notifications.phoneRequired")}</Typography>
                            <br />
                            <Typography component="span" variant="caption" color="text.secondary">
                              {t("notifications.availableForPatients", { count: selectedPatients.filter(p => p.smsConsent && p.phone).length })}
                            </Typography>
                          </Box>
                        }
                      />
                    </MenuItem>
                    <MenuItem value="email">
                      <Checkbox checked={notificationForm.channels.indexOf("email") > -1} />
                      <ListItemText
                        primary={t("notifications.email")}
                        secondary={
                          <Box component="span">
                            <Typography component="span" variant="caption">{t("notifications.emailRequired")}</Typography>
                            <br />
                            <Typography component="span" variant="caption" color="text.secondary">
                              {t("notifications.availableForPatients", { count: selectedPatients.filter(p => p.emailConsent && p.email).length })}
                            </Typography>
                          </Box>
                        }
                      />
                    </MenuItem>
                    <MenuItem value="telegram">
                      <Checkbox checked={notificationForm.channels.indexOf("telegram") > -1} />
                      <ListItemText
                        primary={t("notifications.telegram")}
                        secondary={
                          <Box component="span">
                            <Typography component="span" variant="caption">{t("notifications.telegramRequired")}</Typography>
                            <br />
                            <Typography component="span" variant="caption" color="text.secondary">
                              {t("notifications.availableForPatients", { count: selectedPatients.filter(p => p.telegramConsent && p.telegramUsername).length })}
                            </Typography>
                          </Box>
                        }
                      />
                    </MenuItem>
                  </Select>
                </FormControl>

                <Alert severity="info">
                   {t("notifications.consentWarning")}
                </Alert>
              </Box>
            )}

            {activeStep === 3 && (
              <Box>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Schedule Type</InputLabel>
                  <Select
                    value={notificationForm.scheduleType}
                    onChange={(e) => handleNotificationFormChange("scheduleType", e.target.value)}
                    label="Schedule Type"
                  >
                    <MenuItem value="now">{t("notifications.scheduleNow")}</MenuItem>
                    <MenuItem value="scheduled">{t("notifications.scheduleLater")}</MenuItem>
                    <MenuItem value="recurring">{t("notifications.scheduleRecurring")}</MenuItem>
                  </Select>
                </FormControl>

                {notificationForm.scheduleType === "scheduled" && (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DatePicker
                        label={t("notifications.dateLabel")}
                        value={notificationForm.scheduledDate}
                        onChange={(date) => handleNotificationFormChange("scheduledDate", date)}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TimePicker
                        label="Time"
                        value={notificationForm.scheduledTime}
                        onChange={(time) => handleNotificationFormChange("scheduledTime", time)}
                        renderInput={(params) => <TextField {...params} fullWidth />}
                      />
                    </Grid>
                  </Grid>
                )}

                {notificationForm.scheduleType === "recurring" && (
                  <Box>
                    <FormGroup row sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={notificationForm.recurring.enabled}
                            onChange={(e) => handleNotificationFormChange("recurring", {
                              ...notificationForm.recurring,
                              enabled: e.target.checked
                            })}
                          />
                        }
                        label={t("notifications.enableRecurring")}
                      />
                    </FormGroup>

                    {notificationForm.recurring.enabled && (
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            select
                            label={t("notifications.frequency")}
                            value={notificationForm.recurring.frequency}
                            onChange={(e) => handleNotificationFormChange("recurring", {
                              ...notificationForm.recurring,
                              frequency: e.target.value
                            })}
                          >
                              <MenuItem value="daily">{t("notifications.daily")}</MenuItem>
                              <MenuItem value="weekly">{t("notifications.weekly")}</MenuItem>
                              <MenuItem value="monthly">{t("notifications.monthly")}</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TimePicker
                            label="Time"
                            value={notificationForm.recurring.time}
                            onChange={(time) => handleNotificationFormChange("recurring", {
                              ...notificationForm.recurring,
                              time
                            })}
                            renderInput={(params) => <TextField {...params} fullWidth />}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
                 <Button onClick={handleBack} disabled={activeStep === 0}>
            {t("common.back") || "Back"}
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          <Button
            onClick={activeStep === steps.length - 1 ? sendNotification : handleNext}
            variant="contained"
          >
            {activeStep === steps.length - 1
              ? (notificationForm.scheduleType === "now" ? t("notifications.sendNowButton") : t("notifications.scheduleButton"))
              : t("common.next") || "Next"}
          </Button>
          </DialogActions>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Message Preview</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
              <Typography variant="body1" whiteSpace="pre-wrap">
                {notificationForm.message}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Length: {notificationForm.message.length} characters
              {notificationForm.message.length > 612 && (
                <span style={{ color: 'red' }}> (SMS limit: 612 characters)</span>
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Notification History Dialog */}
        <Dialog
          open={historyDialogOpen}
          onClose={() => setHistoryDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <HistoryIcon sx={{ mr: 1 }} />
              {t("notifications.tabs.history")}
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {historyLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : notificationHistory.length === 0 ? (
               <Alert severity="info">{t("notifications.historyNotFound") || "No notification history found."}</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                       <TableCell>{t("common.date") || "Date"}</TableCell>
                      <TableCell>{t("notifications.table.patient")}</TableCell>
                      <TableCell>{t("notifications.table.message")}</TableCell>
                      <TableCell>{t("notifications.channelsLabel")}</TableCell>
                      <TableCell>{t("notifications.table.status")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedHistory.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{formatDate(item.timestamp)}</TableCell>
                        <TableCell>{item.patientName}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {item.message}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            {item.channels.map(channel => (
                              <Chip
                                key={channel}
                                icon={getChannelIcon(channel)}
                                label={channel}
                                size="small"
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(`notifications.status.${item.status}`) || item.status}
                            size="small"
                            color={getStatusColor(item.status)}
                            icon={
                              item.status === "sent" ? <CheckCircleIcon /> :
                                item.status === "failed" ? <ErrorIcon /> :
                                  <CancelIcon />
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {notificationHistory.length > 0 && !historyLoading && (
              <TablePagination
                component="div"
                count={notificationHistory.length}
                page={historyPage}
                onPageChange={handleHistoryChangePage}
                rowsPerPage={historyRowsPerPage}
                onRowsPerPageChange={handleHistoryChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage={t("common.rowsPerPage") || "Rows per page:"}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryDialogOpen(false)}>{t("common.close") || "Close"}</Button>
          </DialogActions>
        </Dialog>

        {/* Info Alert Snackbar */}
        <Snackbar
          open={selectedPatients.length > 0}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{ mb: 7 }} // Add margin to not overlap with FAB
        >
          <MuiSnackbarAlert severity="info" sx={{ width: '100%' }}>
            {t('notifications.patientsSelectedSnackbar', { count: selectedPatients.length })}
          </MuiSnackbarAlert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
}